import { Box } from '@gemini/core';
import { ArrowLeftIcon } from '@gemini/icons';
import { Button } from '@gemini/ui';
import { useAppContext } from '@mfe-presentation/contexts';
import { getStepInfo } from '@mfe-presentation/hooks/useStepUtils';
import { useTranslation } from '@aviv-unified-frontend/runtime/i18n';
import { NAME_SPACE } from '@mfe-presentation/hooks/useLoadTranslations';

interface INavigation {
    handleClickBackButton: () => void;
}

const Navigation = ({ handleClickBackButton }: INavigation) => {
    const {
        currentStep: { index, name },
        maxDisplayedStep,
        userVerification,
        estimations,
    } = useAppContext();
    const { t } = useTranslation(NAME_SPACE);
    const { maxStepIndex, config } = useAppContext();

    const isUpdatePage = name === 'update';
    const isGetMyEstimationStep = name === 'identity' || name === 'verification';

    const getNextButtonTranlation = () => {
        if (name === 'update') return t('step_update.footer.button.seeNewPrice');
        else if (name === 'verification') return t('common.footer.verifyAndGetMyEstimation');
        else if (name === 'identity') return t('common.footer.getMyEstimation');
        return t('common.footer.next');
    };

    const prevStep = getStepInfo(config, index - 1, maxStepIndex);
    const nextStep = getStepInfo(config, index + 1, maxStepIndex);

    const previousButtonText = t('common.footer.back');
    const nextButtonText = getNextButtonTranlation();

    let previousButtonAriaLabel = previousButtonText;
    let nextButtonAriaLabel = nextButtonText;

    if (prevStep) {
        const ariaLabelBack = t(`step_${prevStep.name}.header.stepper`, {
            replace: { currentStep: prevStep.displayedStepNumber ?? '', maxStep: maxDisplayedStep },
        });
        previousButtonAriaLabel += ` (${ariaLabelBack})`;
    }
    if (nextStep) {
        const ariaLabelNext = t(`step_${nextStep.name}.header.stepper`, {
            replace: {
                currentStep: nextStep?.displayedStepNumber ?? '',
                maxStep: maxDisplayedStep,
            },
        });
        nextButtonAriaLabel += ` (${ariaLabelNext})`;
    }

    const isFetching = userVerification.isFetching || estimations.isFetching;

    return (
        <Box
            id="navigation"
            borderTopWidth="borderWidth.1"
            borderTopStyle="solid"
            borderTopColor="color.border.light.default"
            position="fixed"
            bottom={{
                'breakpoint.base': '48px',
                'breakpoint.sm': '56px',
            }}
            left="0"
            right="0"
            testId="navigation"
            backgroundColor="color.background.default"
        >
            <Box
                alignItems="center"
                display="flex"
                justifyContent={isUpdatePage ? 'end' : 'space-between'}
                gap={{
                    'breakpoint.base': isGetMyEstimationStep ? 'unset' : 'spacing.24',
                    'breakpoint.sm': 'spacing.24',
                }}
                margin="0 auto"
                maxWidth="600px"
                paddingVertical={{
                    'breakpoint.base': isGetMyEstimationStep ? 'spacing.8' : '0',
                    'breakpoint.sm': '0',
                }}
                paddingHorizontal={{
                    'breakpoint.base': 'spacing.16',
                    'breakpoint.sm': 'spacing.24',
                }}
                height={{
                    'breakpoint.base': isGetMyEstimationStep ? '120px' : '64px',
                    'breakpoint.sm': '80px',
                }}
                zIndex="zIndex.1"
                flexDirection={{
                    'breakpoint.base': isGetMyEstimationStep ? 'column-reverse' : 'unset',
                    'breakpoint.sm': 'unset',
                }}
            >
                <Box
                    width="100%"
                    position="absolute"
                    left="spacing.none"
                    bottom={{
                        'breakpoint.base': isGetMyEstimationStep ? '120px' : '64px',
                        'breakpoint.sm': '80px',
                    }}
                    height="72px"
                    unsafe_style={{
                        background:
                            'linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #FFFFFF 100%)',
                        pointerEvents: 'none',
                    }}
                />
                <Box
                    width="100%"
                    position="absolute"
                    left="spacing.none"
                    bottom={{
                        'breakpoint.base': isGetMyEstimationStep ? '120px' : '64px',
                        'breakpoint.sm': '80px',
                    }}
                    height="sizing.16"
                    unsafe_style={{
                        boxShadow: 'inset 0 -5px 5px 0px #f6f6f6',
                        pointerEvents: 'none',
                    }}
                />
                {!isUpdatePage && (
                    <Button
                        aria-label={previousButtonAriaLabel}
                        testId="button-back"
                        type="button"
                        icon={ArrowLeftIcon}
                        variant="tertiary"
                        onPress={handleClickBackButton}
                        isDisabled={isFetching}
                        isFullWidth={{
                            'breakpoint.base': isGetMyEstimationStep,
                            'breakpoint.sm': false,
                        }}
                    >
                        {previousButtonText}
                    </Button>
                )}
                <Button
                    type="submit"
                    aria-label={nextButtonAriaLabel}
                    testId="button-continue"
                    isDisabled={isFetching}
                    isLoading={isFetching}
                    isFullWidth={{
                        'breakpoint.base': isGetMyEstimationStep || isUpdatePage,
                        'breakpoint.sm': false,
                    }}
                >
                    {nextButtonText}
                </Button>
            </Box>
        </Box>
    );
};

export default Navigation;
